import { cleanOptionValue } from './optionAxes';

export type VariantAttributeGroup = { sku: string; [attributeKey: string]: unknown };

export type AxisSelection = Record<string, string | number | undefined>;

export const normalizeAxisValue = (value: unknown) => {
  if (value === undefined || value === null) return '';
  // Variant-defining axes can be enum/lenum attributes, which reach the variant group as raw
  // commercetools objects (`{ label, value }`, `{ key }`, …) — the backend's own
  // `normalizeToLabelValue` exists because that shape occurs in this dataset. The selectable
  // options the shopper picks from are the primitives extracted by that helper (`value ?? key ??
  // id`), so match on the same primitive here. Extracting nothing comparable (only a `label`, or a
  // nested object) collapses to '' — unmatchable — so we report "unavailable" rather than carting
  // the wrong SKU.
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const primitive = record.value ?? record.key ?? record.id;
    if (primitive === undefined || primitive === null || typeof primitive === 'object') return '';
    return cleanOptionValue(String(primitive)).toLowerCase();
  }
  return cleanOptionValue(String(value)).toLowerCase();
};

/**
 * Resolves the SKU whose attributes equal the selection on **every** selectable axis.
 *
 * Returns `null` when nothing matches — callers must surface that as "unavailable" rather than
 * falling back to a near-match. Carting a variant that differs from what the shopper configured
 * ships the wrong product, which is worse than refusing the add.
 */
export const findVariantSkuForSelection = (
  variantGroups: VariantAttributeGroup[] | undefined,
  selection: AxisSelection,
  axisKeys: string[],
): string | null => {
  if (!variantGroups?.length) return null;

  const match = variantGroups.find((group) =>
    axisKeys.every((key) => {
      const selected = normalizeAxisValue(selection[key]);
      // An axis the shopper has not resolved yet cannot be matched, so no variant qualifies.
      return selected !== '' && normalizeAxisValue(group[key]) === selected;
    }),
  );

  return match?.sku ?? null;
};
